/**
 * FogBugz case → ADO Bug schemas (epic PI-SPEC-SW-E311, ER-1666 Package E,
 * contract fogbugz-to-ado 1).
 *
 * A Project Manager brings an existing FogBugz case to the ADO board as a Bug.
 * One case travels like this:
 *
 * 1. SpecTree prepares the case with the PM's own FogBugz key
 *    ({@link fogbugzToAdoPrepareResponseSchema}): the case bundle
 *    ({@link fogbugzToAdoCaseBundleSchema}: events, attachments, EVTX
 *    digests, the `intent.md` slots and Bug-bar result when present, the
 *    linked ADO project and repos), or a refusal
 *    ({@link fogbugzToAdoRefusalSchema}: no key, no PAT, not a Bug, not
 *    linked, repos not set up, already on the board). A Feature case's
 *    refusal carries a hand-off to the ADO Formulator.
 * 2. The page asks the Toolbox to check the repo copies (`checkRepos`) and to
 *    read the slots (`readSlots`): the PM's own Claude Code reads the case
 *    with the code open and prints one `SPECTREE_FOGBUGZ_TO_ADO {json}` line
 *    ({@link fogbugzToAdoSlotsMarkerSchema}): a verdict per bug slot, `found`
 *    with value and evidence or `missing` (a guess never counts as found),
 *    and questions for the reporter.
 * 3. Below the bar, the PM either posts the reporter questions as a FogBugz
 *    note ({@link fogbugzToAdoReporterQuestionsBodySchema}) or fills the gaps
 *    in a short interview (`pm-filled`).
 * 4. The PM confirms the slots ({@link fogbugzToAdoConfirmedSlotsSchema}) and
 *    the Toolbox drafts the Bug (`draftBug`,
 *    {@link fogbugzToAdoDraftMarkerSchema}); the PM picks the area path and
 *    iteration (never inferred; {@link fogbugzToAdoClassificationSchema}).
 * 5. SpecTree creates the Bug, uploads the files, links the case, tags it
 *    `fogbugz-<n>` and posts the note ({@link fogbugzToAdoCreateBodySchema} →
 *    {@link fogbugzToAdoCreateResultSchema}). The Bug text is composed by
 *    `composeFogbugzToAdoBug`, so the page preview and the write match.
 *
 * Request schemas are strict (unknown keys are refused). Response schemas are
 * lenient, so a SpecTree release may add response fields without a contract
 * bump. Marker JSON is untrusted model output: parsed with these schemas and
 * capped. Case text is untrusted data written by others.
 *
 * The slot-id enum and the Bug-bar result are the fogbugz-formulator
 * contract's (`fogbugzFormulatorSlotIdSchema`, `fogbugzBugBarResultSchema`),
 * imported, never re-declared.
 */
import { z } from "zod";
import { FORMULATOR_DESCRIPTION_MAX } from "../formulator/constants.js";
import { GROOMING_ACTIVITY_MAX_CHARS, MAX_GROOMING_REPOS } from "../grooming/constants.js";
import { isoDateTime } from "../zod-helpers.js";
import { fogbugzCaseIdSchema } from "../fogbugz/schemas.js";
import { ADO_AREA_PATHS_MAX, adoAreaPathSchema, adoAreaPathStringSchema, FOGBUGZ_LINK_RESOLUTION_SOURCES, fogbugzLinkRepoRefSchema, } from "../fogbugz/link.js";
import { FOGBUGZ_READ_EVENT_TEXT_MAX } from "../fogbugz/reads.js";
import { FOGBUGZ_EVTX_DIGEST_MAX_CHARS } from "../fogbugz/constants.js";
import { FOGBUGZ_FORMULATOR_IMAGE_MIME_TYPES } from "../fogbugz-formulator/constants.js";
import { fogbugzBugBarResultSchema, fogbugzFormulatorSlotIdSchema, fogbugzFormulatorSlotsSchema, } from "../fogbugz-formulator/schemas.js";
import { helpImageDecodedBytes } from "../help/types.js";
import { FOGBUGZ_TO_ADO_ATTACHMENT_KINDS, FOGBUGZ_TO_ADO_ATTACHMENT_STATUSES, FOGBUGZ_TO_ADO_ATTACHMENTS_MAX, FOGBUGZ_TO_ADO_BAR_MODES, FOGBUGZ_TO_ADO_BUG_SLOT_IDS, FOGBUGZ_TO_ADO_ENVIRONMENT_MIN, FOGBUGZ_TO_ADO_EVIDENCE_MAX, FOGBUGZ_TO_ADO_EVTX_SUMMARY_MAX, FOGBUGZ_TO_ADO_FILE_NAME_MAX, FOGBUGZ_TO_ADO_HISTORY_DIGEST_MAX, FOGBUGZ_TO_ADO_HISTORY_EVENTS_MAX, FOGBUGZ_TO_ADO_HYPERLINK_STATUSES, FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES, FOGBUGZ_TO_ADO_ITERATION_PATH_MAX, FOGBUGZ_TO_ADO_ITERATIONS_MAX, FOGBUGZ_TO_ADO_NOT_A_BUG_CASE, FOGBUGZ_TO_ADO_DUPLICATE_FOUND, FOGBUGZ_TO_ADO_PM_NAME_MAX, FOGBUGZ_TO_ADO_PROXY_PATH_MAX, FOGBUGZ_TO_ADO_REFUSAL_CODES, FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX, FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX, FOGBUGZ_TO_ADO_REPRO_STEP_MAX, FOGBUGZ_TO_ADO_REPRO_STEPS_MAX, FOGBUGZ_TO_ADO_REPRO_STEPS_MIN, FOGBUGZ_TO_ADO_RUN_PHASES, FOGBUGZ_TO_ADO_SCREENSHOT_DESCRIPTION_MAX, FOGBUGZ_TO_ADO_SEVERITIES, FOGBUGZ_TO_ADO_SLOT_STATUSES, FOGBUGZ_TO_ADO_SLOT_VALUE_MAX, FOGBUGZ_TO_ADO_TEXT_SECTION_MAX, FOGBUGZ_TO_ADO_TITLE_MAX, isFogbugzToAdoBugSlotId, } from "./constants.js";
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An ISO 8601 timestamp as JSON carries a Date. */
const isoDateTimeSchema = isoDateTime({ offset: true });
/** A positive FogBugz id (`ixProject`, `ixArea`). */
const fogbugzIdSchema = z.number().int().positive();
/**
 * The slot-id enum: the fogbugz-formulator contract's
 * `fogbugzFormulatorSlotIdSchema` itself (Intent v1 slot ids), not a copy.
 * Verdicts narrow it to the bug slots ({@link fogbugzToAdoBugSlotIdSchema}).
 */
export const fogbugzToAdoSlotIdSchema = fogbugzFormulatorSlotIdSchema;
/** One of the eleven bug slots (`FOGBUGZ_TO_ADO_BUG_SLOT_IDS`), checked against the fogbugz-formulator enum. */
export const fogbugzToAdoBugSlotIdSchema = fogbugzToAdoSlotIdSchema.refine(isFogbugzToAdoBugSlotId, {
    message: "only the bug slots are read from a case",
});
/** A case attachment file name as FogBugz reports it. */
export const fogbugzToAdoFileNameSchema = z.string().trim().min(1).max(FOGBUGZ_TO_ADO_FILE_NAME_MAX);
/** A PM's display name, for the provenance lines and notes. */
export const fogbugzToAdoPmNameSchema = z.string().trim().min(1).max(FOGBUGZ_TO_ADO_PM_NAME_MAX);
/** The ADO Bug's severity (`Microsoft.VSTS.Common.Severity`). */
export const fogbugzToAdoSeveritySchema = z.enum(FOGBUGZ_TO_ADO_SEVERITIES);
/** An ADO iteration path (`Proj\Sprint 12`, or the project root `Proj`). */
export const fogbugzToAdoIterationPathSchema = z.string().min(1).max(FOGBUGZ_TO_ADO_ITERATION_PATH_MAX);
/** An ADO Bug as the tool reports it: its id and web URL. */
export const fogbugzToAdoBugRefSchema = z.object({
    id: z.number().int().positive(),
    url: z.string().min(1),
});
// ---------------------------------------------------------------------------
// The case bundle
// ---------------------------------------------------------------------------
/** One case event, oldest first: when, who, what kind (FogBugz verb) and its text (capped). */
export const fogbugzToAdoEventSchema = z.object({
    at: isoDateTimeSchema.nullable(),
    person: z.string().nullable(),
    kind: z.string(),
    text: z.string().max(FOGBUGZ_READ_EVENT_TEXT_MAX),
});
/**
 * One file on the case, by position (`index`, what create's
 * `attachmentIndexes` refers to), name, kind and size (0 = not known: FogBugz
 * does not report sizes, so only files SpecTree read while preparing have
 * one). No bytes: the page reads them from SpecTree when it needs them,
 * through `proxyPath`, SpecTree's authenticated FogBugz attachment proxy
 * (relative to `/api/v1`, no credential in it); absent when FogBugz gave no
 * download address for the file.
 */
export const fogbugzToAdoAttachmentSchema = z.object({
    index: z.number().int().min(0),
    fileName: fogbugzToAdoFileNameSchema,
    kind: z.enum(FOGBUGZ_TO_ADO_ATTACHMENT_KINDS),
    sizeBytes: z.number().int().min(0),
    proxyPath: z.string().min(1).max(FOGBUGZ_TO_ADO_PROXY_PATH_MAX).optional(),
});
/** The deterministic digest of one `.evtx` attachment (no AI on the server). */
export const fogbugzToAdoEvtxDigestSchema = z.object({
    fileName: fogbugzToAdoFileNameSchema,
    digestMarkdown: z.string().min(1).max(FOGBUGZ_EVTX_DIGEST_MAX_CHARS),
    /** True when parsing stopped early at a record-count or time limit. */
    truncated: z.boolean(),
});
/**
 * The case's `intent.md`, when it has one: `source` is its front-matter value
 * (`fogbugz-web-formulator`, `fogbugz-toolbox-formulator`, ...) and `slots`
 * the Intent v1 slot values it carries. A Formulator case with complete
 * coverage is read from these slots directly.
 */
export const fogbugzToAdoIntentSchema = z
    .object({
    present: z.boolean(),
    source: z.string().min(1).max(100).optional(),
    slots: fogbugzFormulatorSlotsSchema.optional(),
})
    .superRefine((intent, ctx) => {
    if (!intent.present && (intent.source !== undefined || intent.slots !== undefined)) {
        ctx.addIssue({ code: "custom", path: ["present"], message: "a case without intent.md has no source or slots" });
    }
});
/**
 * Where the Bug lands, from the FogBugz ↔ ADO project link (epic
 * PI-SPEC-SW-E308): the ADO project, its default area path (a suggestion the
 * PM confirms, never applied silently) and whether an area override or the
 * project default resolved it.
 */
export const fogbugzToAdoTargetSchema = z.object({
    project: z.string().min(1),
    defaultAreaPath: z.string().nullable(),
    resolvedFrom: z.enum(FOGBUGZ_LINK_RESOLUTION_SOURCES),
});
/**
 * Everything the tool knows about one case, read with the PM's own FogBugz
 * key. `bar` is the Bug-bar result for a case whose `intent.md` coverage
 * could be checked (the fogbugz-formulator contract's
 * `fogbugzBugBarResultSchema`), null for a legacy case without one. Case text
 * is untrusted data.
 */
export const fogbugzToAdoCaseBundleSchema = z.object({
    caseId: fogbugzCaseIdSchema,
    caseUrl: z.string().min(1),
    title: z.string(),
    category: z.string().nullable(),
    fogbugzProject: z.object({ id: fogbugzIdSchema, name: z.string() }),
    fogbugzArea: z.object({ id: fogbugzIdSchema, name: z.string() }).nullable(),
    ado: fogbugzToAdoTargetSchema,
    repos: z.array(fogbugzLinkRepoRefSchema).max(MAX_GROOMING_REPOS),
    events: z.array(fogbugzToAdoEventSchema).max(FOGBUGZ_TO_ADO_HISTORY_EVENTS_MAX),
    attachments: z.array(fogbugzToAdoAttachmentSchema).max(FOGBUGZ_TO_ADO_ATTACHMENTS_MAX),
    evtxDigests: z.array(fogbugzToAdoEvtxDigestSchema),
    intent: fogbugzToAdoIntentSchema,
    bar: fogbugzBugBarResultSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Prepare refusal and the ADO Formulator hand-off
// ---------------------------------------------------------------------------
/**
 * A Feature case's hand-off to the ADO Formulator (owner decision
 * 2026-10-29): "Open in ADO Formulator" opens its start form with a User
 * Story, the linked ADO project (when the link resolves) and the description
 * built by `composeFormulatorHandoff` prefilled. The PM still clicks Start.
 */
export const fogbugzToAdoHandoffSchema = z.object({
    kind: z.literal("story"),
    adoProject: z.string().min(1).optional(),
    description: z.string().min(1).max(FORMULATOR_DESCRIPTION_MAX),
});
/**
 * Why a case cannot be brought to the board, in one plain sentence. Only
 * `DUPLICATE_FOUND` carries `existing` (the Bug already on the board); only
 * `NOT_A_BUG_CASE` carries `category` (the FogBugz category) and, for a
 * Feature case only, `handoff`. An Inquiry case gets the message alone.
 */
export const fogbugzToAdoRefusalSchema = z
    .object({
    code: z.enum(FOGBUGZ_TO_ADO_REFUSAL_CODES),
    message: z.string().min(1),
    existing: fogbugzToAdoBugRefSchema.optional(),
    category: z.string().min(1).max(200).optional(),
    handoff: fogbugzToAdoHandoffSchema.optional(),
})
    .superRefine((refusal, ctx) => {
    if (refusal.existing !== undefined && refusal.code !== FOGBUGZ_TO_ADO_DUPLICATE_FOUND) {
        ctx.addIssue({ code: "custom", path: ["existing"], message: "only DUPLICATE_FOUND names an existing Bug" });
    }
    if (refusal.category !== undefined && refusal.code !== FOGBUGZ_TO_ADO_NOT_A_BUG_CASE) {
        ctx.addIssue({ code: "custom", path: ["category"], message: "only NOT_A_BUG_CASE names a category" });
    }
    if (refusal.handoff !== undefined && refusal.code !== FOGBUGZ_TO_ADO_NOT_A_BUG_CASE) {
        ctx.addIssue({ code: "custom", path: ["handoff"], message: "only NOT_A_BUG_CASE carries a hand-off" });
    }
});
/** What preparing a case returns: the bundle, or why the case cannot come to the board. */
export const fogbugzToAdoPrepareResponseSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("ready"), bundle: fogbugzToAdoCaseBundleSchema }),
    z.object({ status: z.literal("refused"), refusal: fogbugzToAdoRefusalSchema }),
]);
// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------
/**
 * Claude's verdict on one bug slot. `found` needs the value and the evidence
 * (where in the case it is: `event 3`, `intent.md`, `screenshot login.png`);
 * `missing` carries no value, because a guess never counts as filled (the
 * Intent v1 rule "unknown never fills a required slot").
 */
export const fogbugzToAdoSlotVerdictSchema = z
    .object({
    slot: fogbugzToAdoBugSlotIdSchema,
    status: z.enum(FOGBUGZ_TO_ADO_SLOT_STATUSES),
    value: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_SLOT_VALUE_MAX).optional(),
    evidence: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_EVIDENCE_MAX).optional(),
})
    .strict()
    .superRefine((verdict, ctx) => {
    if (verdict.status === "found") {
        if (verdict.value === undefined) {
            ctx.addIssue({ code: "custom", path: ["value"], message: "a found slot needs its value" });
        }
        if (verdict.evidence === undefined) {
            ctx.addIssue({ code: "custom", path: ["evidence"], message: "a found slot needs its evidence" });
        }
    }
    else if (verdict.value !== undefined) {
        ctx.addIssue({ code: "custom", path: ["value"], message: "a missing slot has no value: a guess never fills it" });
    }
});
/** One verdict per bug slot, each exactly once (any order). */
export const fogbugzToAdoSlotVerdictsSchema = z
    .array(fogbugzToAdoSlotVerdictSchema)
    .length(FOGBUGZ_TO_ADO_BUG_SLOT_IDS.length)
    .superRefine((verdicts, ctx) => {
    const seen = new Set();
    verdicts.forEach((verdict, index) => {
        if (seen.has(verdict.slot)) {
            ctx.addIssue({ code: "custom", path: [index, "slot"], message: `${verdict.slot} has more than one verdict` });
        }
        seen.add(verdict.slot);
    });
});
/** A question for the case's reporter about one missing bug slot. */
export const fogbugzToAdoReporterQuestionSchema = z
    .object({
    slot: fogbugzToAdoBugSlotIdSchema,
    question: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX),
})
    .strict();
/** At most {@link FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX} reporter questions. */
const reporterQuestionsSchema = z
    .array(fogbugzToAdoReporterQuestionSchema)
    .max(FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX, `at most ${String(FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX)} questions`);
/**
 * The JSON after `SPECTREE_FOGBUGZ_TO_ADO ` on a slots run's result line: a
 * verdict per bug slot and questions for the reporter, each about a slot
 * marked `missing`.
 */
export const fogbugzToAdoSlotsMarkerSchema = z
    .object({
    slots: fogbugzToAdoSlotVerdictsSchema,
    reporterQuestions: reporterQuestionsSchema,
})
    .strict()
    .superRefine((marker, ctx) => {
    const missing = new Set(marker.slots.filter((verdict) => verdict.status === "missing").map((v) => v.slot));
    marker.reporterQuestions.forEach((question, index) => {
        if (!missing.has(question.slot)) {
            ctx.addIssue({
                code: "custom",
                path: ["reporterQuestions", index, "slot"],
                message: `${question.slot} is not missing, so the reporter is not asked about it`,
            });
        }
    });
});
/** What the PM added for one missing slot in the short interview (barMode `pm-filled`). */
export const fogbugzToAdoPmAdditionSchema = z
    .object({
    slot: fogbugzToAdoBugSlotIdSchema,
    text: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_SLOT_VALUE_MAX),
})
    .strict();
/** The PM's additions, at most one per slot. */
export const fogbugzToAdoPmAdditionsSchema = z
    .array(fogbugzToAdoPmAdditionSchema)
    .max(FOGBUGZ_TO_ADO_BUG_SLOT_IDS.length)
    .superRefine((additions, ctx) => {
    const seen = new Set();
    additions.forEach((addition, index) => {
        if (seen.has(addition.slot)) {
            ctx.addIssue({ code: "custom", path: [index, "slot"], message: `${addition.slot} has more than one addition` });
        }
        seen.add(addition.slot);
    });
});
/**
 * Every slot is covered: found in the case, or filled by a PM addition.
 * Reports each slot left uncovered on `ctx`.
 */
function checkCovered(slots, additions, ctx, path) {
    const added = new Set(additions.map((addition) => addition.slot));
    slots.forEach((verdict, index) => {
        if (verdict.status === "missing" && !added.has(verdict.slot)) {
            ctx.addIssue({ code: "custom", path: [...path, index], message: `${verdict.slot} is missing and the PM added nothing` });
        }
    });
    const missing = new Set(slots.filter((verdict) => verdict.status === "missing").map((verdict) => verdict.slot));
    additions.forEach((addition, index) => {
        if (!missing.has(addition.slot)) {
            ctx.addIssue({
                code: "custom",
                path: ["pmAdditions", index, "slot"],
                message: `${addition.slot} was found in the case; the PM adds only what is missing`,
            });
        }
    });
}
/**
 * The slots the PM confirmed and what they added for the missing ones (the
 * draft input). Every missing slot needs an addition, and an addition only
 * fills a missing slot.
 */
export const fogbugzToAdoConfirmedSlotsSchema = z
    .object({
    slots: fogbugzToAdoSlotVerdictsSchema,
    pmAdditions: fogbugzToAdoPmAdditionsSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    checkCovered(body.slots, body.pmAdditions, ctx, ["slots"]);
});
// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------
/** One event-log summary in the draft (System Info), written from the deterministic digest. */
export const fogbugzToAdoEvtxSummarySchema = z
    .object({
    fileName: fogbugzToAdoFileNameSchema,
    text: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_EVTX_SUMMARY_MAX),
})
    .strict();
/** A written description of one case image, so an agent reading the Bug gets what the image shows. */
export const fogbugzToAdoScreenshotDescriptionSchema = z
    .object({
    fileName: fogbugzToAdoFileNameSchema,
    description: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_SCREENSHOT_DESCRIPTION_MAX),
})
    .strict();
/**
 * The drafted Bug: plain text, composed into ADO HTML by
 * `composeFogbugzToAdoBug`. It meets the ADO grooming bar for a Bug by shape:
 * at least two numbered repro steps, expected and actual, an environment of
 * at least ten characters, a severity. `screenshotDescriptions` has no count
 * cap (owner decision 2026-10-29).
 */
export const fogbugzToAdoDraftSchema = z
    .object({
    title: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_TITLE_MAX),
    reproSteps: z
        .array(z.string().trim().min(1).max(FOGBUGZ_TO_ADO_REPRO_STEP_MAX))
        .min(FOGBUGZ_TO_ADO_REPRO_STEPS_MIN, `at least ${String(FOGBUGZ_TO_ADO_REPRO_STEPS_MIN)} repro steps`)
        .max(FOGBUGZ_TO_ADO_REPRO_STEPS_MAX),
    expected: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_TEXT_SECTION_MAX),
    actual: z.string().trim().min(1).max(FOGBUGZ_TO_ADO_TEXT_SECTION_MAX),
    environment: z.string().trim().min(FOGBUGZ_TO_ADO_ENVIRONMENT_MIN).max(FOGBUGZ_TO_ADO_TEXT_SECTION_MAX),
    evtxSummaries: z.array(fogbugzToAdoEvtxSummarySchema),
    severity: fogbugzToAdoSeveritySchema,
    historyDigest: z.string().trim().max(FOGBUGZ_TO_ADO_HISTORY_DIGEST_MAX),
    screenshotDescriptions: z.array(fogbugzToAdoScreenshotDescriptionSchema),
})
    .strict();
/** The JSON after `SPECTREE_FOGBUGZ_TO_ADO ` on a draft run's result line. */
export const fogbugzToAdoDraftMarkerSchema = z.object({ draft: fogbugzToAdoDraftSchema }).strict();
// ---------------------------------------------------------------------------
// Reporter questions (below the bar)
// ---------------------------------------------------------------------------
/**
 * The body that posts questions for the reporter as a FogBugz note (composed
 * by `composeReporterQuestionsNote`). 1 to
 * {@link FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX} questions; nothing is written
 * to ADO.
 */
export const fogbugzToAdoReporterQuestionsBodySchema = z
    .object({
    questions: z
        .array(fogbugzToAdoReporterQuestionSchema)
        .min(1)
        .max(FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX, `at most ${String(FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX)} questions`),
})
    .strict();
// ---------------------------------------------------------------------------
// Classification (area path and iteration pickers)
// ---------------------------------------------------------------------------
/**
 * One iteration the PM may pick. `isRoot` marks the project root: the Bug
 * goes to the backlog with no sprint (owner decision 2026-10-29); the picker
 * shows it first as "<project> (no sprint)". Nothing is preselected.
 */
export const fogbugzToAdoIterationSchema = z.object({
    path: fogbugzToAdoIterationPathSchema,
    startDate: isoDateTimeSchema.optional(),
    finishDate: isoDateTimeSchema.optional(),
    isRoot: z.boolean().optional(),
});
/** The area paths and iterations of the target ADO project, at most one of them the root. */
export const fogbugzToAdoClassificationSchema = z
    .object({
    areaPaths: z.array(adoAreaPathSchema).max(ADO_AREA_PATHS_MAX),
    /** True when there were more area paths than {@link ADO_AREA_PATHS_MAX}. */
    truncated: z.boolean().optional(),
    /**
     * The project root first, then the other iterations newest first (by
     * start, else finish date; undated ones after the dated ones), at most
     * {@link FOGBUGZ_TO_ADO_ITERATIONS_MAX}.
     */
    iterations: z.array(fogbugzToAdoIterationSchema).max(FOGBUGZ_TO_ADO_ITERATIONS_MAX),
    /** True when the project has more iterations than the list carries (the oldest are left out). */
    iterationsTruncated: z.boolean().optional(),
})
    .superRefine((classification, ctx) => {
    if (classification.iterations.filter((iteration) => iteration.isRoot === true).length > 1) {
        ctx.addIssue({ code: "custom", path: ["iterations"], message: "only one iteration is the project root" });
    }
});
// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------
/**
 * The body that creates the Bug: the (reviewed) draft, the confirmed slots,
 * the area path and iteration the PM picked (never inferred), the case files
 * to upload (by `index`), how the case met the bar and what the PM added.
 * `complete` means every slot was found and nothing was added; `pm-filled`
 * means the PM filled at least one missing slot.
 */
export const fogbugzToAdoCreateBodySchema = z
    .object({
    draft: fogbugzToAdoDraftSchema,
    confirmedSlots: fogbugzToAdoSlotVerdictsSchema,
    areaPath: adoAreaPathStringSchema,
    iterationPath: fogbugzToAdoIterationPathSchema,
    attachmentIndexes: z.array(z.number().int().min(0)).max(FOGBUGZ_TO_ADO_ATTACHMENTS_MAX),
    barMode: z.enum(FOGBUGZ_TO_ADO_BAR_MODES),
    pmAdditions: fogbugzToAdoPmAdditionsSchema,
})
    .strict()
    .superRefine((body, ctx) => {
    checkCovered(body.confirmedSlots, body.pmAdditions, ctx, ["confirmedSlots"]);
    if (body.barMode === "complete" && body.pmAdditions.length > 0) {
        ctx.addIssue({ code: "custom", path: ["barMode"], message: "a complete case has no PM additions" });
    }
    if (body.barMode === "pm-filled" && body.pmAdditions.length === 0) {
        ctx.addIssue({ code: "custom", path: ["barMode"], message: "pm-filled needs at least one PM addition" });
    }
    const seen = new Set();
    body.attachmentIndexes.forEach((attachmentIndex, index) => {
        if (seen.has(attachmentIndex)) {
            ctx.addIssue({ code: "custom", path: ["attachmentIndexes", index], message: "an attachment is listed twice" });
        }
        seen.add(attachmentIndex);
    });
});
/** What happened to one case file on create. */
export const fogbugzToAdoAttachmentResultSchema = z.object({
    fileName: z.string().min(1),
    status: z.enum(FOGBUGZ_TO_ADO_ATTACHMENT_STATUSES),
    message: z.string().optional(),
});
/**
 * What create returns: the new Bug, each file's outcome (`too-large` files
 * stay on the FogBugz case), whether the "ADO Bug #N created" note was
 * posted to the case and whether the Bug's Hyperlink back to the case was
 * added (absent from an older SpecTree). `otherCopies` lists any other work
 * item copied from the same case that the duplicate check found after the
 * Bug was created (two creates at once); nothing is deleted, the PM decides.
 * A failed note, link or file never undoes the Bug.
 */
export const fogbugzToAdoCreateResultSchema = z
    .object({
    ado: fogbugzToAdoBugRefSchema,
    attachments: z.array(fogbugzToAdoAttachmentResultSchema),
    fogbugzNote: z.enum(["posted", "failed"]),
    hyperlink: z.enum(FOGBUGZ_TO_ADO_HYPERLINK_STATUSES).optional(),
    otherCopies: z.array(fogbugzToAdoBugRefSchema).optional(),
})
    .loose();
// ---------------------------------------------------------------------------
// Bridge inputs (SpecTree Toolbox)
// ---------------------------------------------------------------------------
/**
 * One case image as the page hands it to the Toolbox: file name, image type
 * and bytes as base64, at most {@link FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES}
 * decoded. Every image on the case within that cap is sent (no count cap);
 * a larger one is not described, and not attached either (the per-file
 * attachment cap is the same): it stays on the FogBugz case.
 */
export const fogbugzToAdoImageInputSchema = z
    .object({
    fileName: fogbugzToAdoFileNameSchema,
    mime: z.enum(FOGBUGZ_FORMULATOR_IMAGE_MIME_TYPES),
    base64: z.string().min(1),
})
    .strict()
    .superRefine((image, ctx) => {
    const bytes = helpImageDecodedBytes(image.base64);
    if (bytes < 0) {
        ctx.addIssue({ code: "custom", path: ["base64"], message: "base64 must be base64" });
    }
    else if (bytes === 0 || bytes > FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES) {
        ctx.addIssue({
            code: "custom",
            path: ["base64"],
            message: `an image must be 1 byte to ${String(FOGBUGZ_TO_ADO_IMAGE_MAX_BYTES)} bytes`,
        });
    }
});
/** `checkRepos` input: the linked ADO project and its mapped repos. */
export const fogbugzToAdoCheckReposInputSchema = z
    .object({
    project: z.string().min(1),
    repos: z.array(fogbugzLinkRepoRefSchema).min(1).max(MAX_GROOMING_REPOS),
})
    .strict();
/** `readSlots` input: the case bundle, every case image (no count cap) and the PM's name. */
export const fogbugzToAdoReadSlotsInputSchema = z
    .object({
    bundle: fogbugzToAdoCaseBundleSchema,
    images: z.array(fogbugzToAdoImageInputSchema),
    pmName: fogbugzToAdoPmNameSchema,
})
    .strict();
/** `draftBug` input: the bundle, the images, the confirmed slots, the PM's additions and name. */
export const fogbugzToAdoDraftBugInputSchema = z
    .object({
    bundle: fogbugzToAdoCaseBundleSchema,
    images: z.array(fogbugzToAdoImageInputSchema),
    confirmedSlots: fogbugzToAdoSlotVerdictsSchema,
    pmAdditions: fogbugzToAdoPmAdditionsSchema,
    pmName: fogbugzToAdoPmNameSchema,
})
    .strict()
    .superRefine((input, ctx) => {
    checkCovered(input.confirmedSlots, input.pmAdditions, ctx, ["confirmedSlots"]);
});
/**
 * A `progress` event (`fogbugzToAdo.onProgress`): the run's phase for one
 * case, an optional short activity line and when the phase started. Pages
 * parse with `safeParse` and ignore events that do not parse.
 */
export const fogbugzToAdoProgressEventSchema = z.object({
    type: z.literal("progress"),
    caseId: fogbugzCaseIdSchema,
    phase: z.enum(FOGBUGZ_TO_ADO_RUN_PHASES),
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
    startedAt: z.string(),
});
//# sourceMappingURL=schemas.js.map